<div class="task-quick-create-modal" *ngIf="visible">
  <div class="modal-backdrop" (click)="onCancel()"></div>

  <div class="modal-container">
    <!-- Header -->
    <div class="modal-header">
      <h2 class="modal-title">
        <!-- <i class="pi pi-plus-circle"></i> -->
        Create Task
        <!-- <span *ngIf="context?.entityDisplayName" class="context-badge">
          for {{ context.entityDisplayName }}
        </span> -->
      </h2>
      <button type="button" class="close-button" (click)="onCancel()" [disabled]="isSaving">
        <i class="pi pi-times"></i>
      </button>
    </div>

    <!-- Loading State -->
    <div *ngIf="isLoading" class="loading-container">
      <p-progressSpinner></p-progressSpinner>
      <p>Loading...</p>
    </div>

    <!-- Form -->
    <form [formGroup]="taskForm" (ngSubmit)="onSubmit()" *ngIf="!isLoading">
      <div class="modal-body">
        <!-- Entity Context Info -->
        <div class="context-info" *ngIf="context">
          <div class="info-row">
            <i class="pi pi-link"></i>
            <span>
              Primary link:
              <strong>{{ context.entityDisplayName || (context.entityType + ' #' + context.entityId) }}</strong>
            </span>
          </div>
          <div class="info-row related-links" *ngIf="context.relatedEntities?.length">
            <i class="pi pi-sitemap"></i>
            <span>
              Related:
              <strong *ngFor="let rel of context.relatedEntities; let last = last">
                {{ rel.metadata?.displayName || (rel.entity_type + ' #' + rel.entity_id) }}<span *ngIf="!last">, </span>
              </strong>
            </span>
          </div>
        </div>

        <!-- Task Type Group (Filter) -->
        <div class="form-group">
          <label for="task_type_group_id">
            Task Type Group
            <!-- <span class="help-text">(Optional - filter task types)</span> -->
          </label>
          <p-dropdown
            id="task_type_group_id"
            formControlName="task_type_group_id"
            [options]="taskTypeGroups"
            optionLabel="label"
            optionValue="value"
            placeholder="All Groups"
            (onChange)="onTaskTypeGroupChange()"
            [disabled]="isSaving"
            appendTo="body"
            class="severity"
          >
            <ng-template let-option pTemplate="item">
              <div class="task-group-option">
                <!-- <i *ngIf="option.icon" [class]="option.icon" [style.color]="option.color"></i> -->
                <span [style.color]="option.color">{{ option.label }}</span>
              </div>
            </ng-template>
            <ng-template pTemplate="selectedItem" let-option>
              <div class="task-group-option" *ngIf="option">
                <!-- <i *ngIf="option.icon" [class]="option.icon" [style.color]="option.color"></i> -->
                <span [style.color]="option.color">{{ option.label }}</span>
              </div>
            </ng-template>
          </p-dropdown>
        </div>

        <!-- Task Type (Required) -->
        <div class="form-group" [class.has-error]="isFieldInvalid('task_type_id')">
          <label for="task_type_id"> Task Type <span class="required">*</span> </label>
          <p-dropdown
            id="task_type_id"
            formControlName="task_type_id"
            [options]="taskTypes"
            optionLabel="label"
            optionValue="value"
            placeholder="Select task type"
            [filter]="true"
            filterPlaceholder="Search task types"
            (onChange)="onTaskTypeChange()"
            [disabled]="isSaving"
            appendTo="body"
            class="severity"
          >
            <ng-template let-option pTemplate="item">
              <div class="task-type-option">
                <span>{{ option.label }}</span>
                <span class="group-name">{{ option.groupName }}</span>
              </div>
            </ng-template>
          </p-dropdown>
          <small class="error-message" *ngIf="isFieldInvalid('task_type_id')">
            {{ getFieldError('task_type_id') }}
          </small>
        </div>

        <!-- Subject (Required) -->
        <div class="form-group" [class.has-error]="isFieldInvalid('subject')">
          <label for="subject"> Subject <span class="required">*</span> </label>
          <input
            id="subject"
            type="text"
            pInputText
            formControlName="subject"
            placeholder="Brief description of the task"
            [disabled]="isSaving"
            class="w-full txt-area"
          />
          <small class="error-message" *ngIf="isFieldInvalid('subject')">
            {{ getFieldError('subject') }}
          </small>
        </div>

        <!-- Priority -->
        <div class="form-group">
          <label for="priority">Priority</label>
          <p-dropdown
            id="priority"
            formControlName="priority"
            [options]="priorities"
            optionLabel="label"
            optionValue="value"
            placeholder="Select priority"
            [disabled]="isSaving"
            appendTo="body"
            class="severity"
          >
            <ng-template let-option pTemplate="item">
              <div class="priority-option">
                <span class="priority-dot" [style.background-color]="option.color"></span>
                {{ option.label }}
              </div>
            </ng-template>
            <ng-template pTemplate="selectedItem" let-option>
              <div class="priority-option" *ngIf="getPriorityDisplayOption(option) as opt">
                <span class="priority-dot" [style.background-color]="opt.color"></span>
                {{ opt.label }}
              </div>
            </ng-template>
          </p-dropdown>
        </div>

        <!-- Status (Auto-populated based on task type) -->
        <div class="form-group" [class.has-error]="isFieldInvalid('status_id')">
          <label for="status_id"> Status <span class="required">*</span> </label>
          <p-dropdown
            id="status_id"
            formControlName="status_id"
            [options]="taskStatuses"
            optionLabel="name"
            optionValue="id"
            placeholder="Select status"
            [disabled]="isSaving || taskStatuses.length === 0"
            appendTo="body"
            class="severity"
          >
            <ng-template let-option pTemplate="item">
              <div class="status-option">
                <span class="status-dot" [style.background-color]="option.color"></span>
                {{ option.name }}
              </div>
            </ng-template>
          </p-dropdown>
          <small class="error-message" *ngIf="isFieldInvalid('status_id')">
            {{ getFieldError('status_id') }}
          </small>
        </div>

        <!-- Projects -->
        <div class="form-group">
          <label for="project_ids">Projects <span class="help-text">(Optional)</span></label>
          <p-multiSelect
            id="project_ids"
            formControlName="project_ids"
            [options]="projects"
            optionLabel="label"
            optionValue="value"
            placeholder="Add to projects..."
            [disabled]="isSaving"
            appendTo="body"
            class="projects"
          >
          </p-multiSelect>
        </div>

        <!-- Entity Links -->
        <div class="form-group">
          <label
            >Entity Links
            <span class="help-text">(Link to patient, encounter, claim, etc.)</span></label
          >
          <div class="entity-links-container" formArrayName="entities">
            <div
              *ngFor="let entity of entitiesArray.controls; let i = index"
              [formGroupName]="i"
              class="entity-link-row"
            >
              <p-dropdown
                formControlName="entity_type"
                [options]="entityTypeOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Type"
                appendTo="body"
                [disabled]="isSaving"
                class="severity entity-links-width"
              ></p-dropdown>
              <p-autoComplete
                [ngModel]="entityDisplayValues[i]"
                (ngModelChange)="onEntityIdChange($event, i)"
                [ngModelOptions]="{ standalone: true }"
                [suggestions]="entitySuggestions[i] || []"
                (completeMethod)="searchEntitySuggestions($event.query, i)"
                (onSelect)="onEntitySelected($event, i)"
                field="display_name"
                placeholder="Search or enter ID"
                [disabled]="isSaving"
                styleClass="entity-id-autocomplete"
                appendTo="body"
                class="entity-links-width"
              ></p-autoComplete>
              <p-dropdown
                formControlName="link_type"
                [options]="linkTypeOptions"
                optionLabel="label"
                optionValue="value"
                appendTo="body"
                [disabled]="isSaving"
                class="severity entity-links-width"
              ></p-dropdown>
              <!-- <button
                type="button"
                (click)="removeEntityLink(i)"
                [disabled]="isSaving"
                title="Remove link"
              > -->
                <i class="pi pi-trash delete-entity" (click)="removeEntityLink(i)"
                ></i>
              <!-- </button> -->
            </div>
            <button
              type="button"
              class="add-entity-btn"
              (click)="addEntityLink()"
              [disabled]="isSaving"
            >
              <i class="pi pi-plus"></i> Add Entity Link
            </button>
          </div>
        </div>

        <!-- Toggle Full Form -->
        <!-- <div class="toggle-section">
          <button
            type="button"
            class="toggle-button"
            (click)="toggleFullForm()"
            [disabled]="isSaving"
          >
          <span>{{ showFullForm ? 'Hide' : 'Show' }} Additional Options</span>
          <i class="pi" [ngClass]="showFullForm ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
          </button>
        </div> -->

        <!-- Additional Fields (Expandable) -->
        <div class="additional-fields">
          <!-- Description -->
          <div class="form-group">
            <label for="description">Description</label>
            <textarea
              id="description"
              pInputTextarea
              formControlName="description"
              rows="3"
              placeholder="Detailed task description"
              [disabled]="isSaving"
              class="w-full "
            >
            </textarea>
          </div>

          <!-- Assigned To -->
          <div class="form-group">
            <label for="assigned_to">Assign To</label>
            <p-dropdown
              id="assigned_to"
              formControlName="assigned_to"
              [options]="users"
              optionLabel="label"
              optionValue="value"
              placeholder="Select User"
              [filter]="true"
              filterBy="label"
              [disabled]="isSaving"
              appendTo="body"
              class="w-full severity"
            >
              <ng-template pTemplate="item" let-option>
                <div class="user-option">
                  <!-- <img
                    *ngIf="option.profilePictureUrl"
                    [src]="option.profilePictureUrl"
                    alt="Profile"
                    class="avatar"
                  /> -->
                  <span class="trim-label" title="{{ option.label }}">{{ option.label }}</span>
                  <span class="role-badge" *ngIf="option.roleName">{{ option.roleName }}</span>
                </div>
              </ng-template>
            </p-dropdown>
          </div>

          <!-- Due Date -->
          <div class="form-group">
            <label for="due_at">Due Date</label>
            <p-calendar
              id="due_at"
              formControlName="due_at"
              [showIcon]="true"
              dateFormat="yy-mm-dd"
              placeholder="Select due date"
              [disabled]="isSaving"
              [readonlyInput]="true"
              appendTo="body"
              class="due-date-calendar"
            >
            </p-calendar>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <button type="button" class="cancel-btn" (click)="onCancel()" [disabled]="isSaving">
          Cancel
        </button>
        <button type="submit" class="save-btn" [disabled]="taskForm.invalid || isSaving">
          <!-- <i class="pi pi-spin pi-spinner" *ngIf="isSaving"></i>
          <i class="pi pi-check" *ngIf="!isSaving"></i> -->
          Create
        </button>
      </div>
    </form>
  </div>
</div>
